import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { AUTH_CARD, AUTH_INPUT, AUTH_LINK, AUTH_TITLE, AuthAlert, AuthLabel, AuthShell } from '../components/AuthShell';
import { Button, Field, Input } from '../components/ui';

export function LoginPage() {
  const { t } = useI18n();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <form onSubmit={onSubmit} className={AUTH_CARD}>
        <h2 className={AUTH_TITLE}>{t('auth.welcome')}</h2>

        <div className="space-y-4">
          <Field label={<AuthLabel>{t('auth.emailLabel')}</AuthLabel>} required>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              autoFocus
              dir="ltr"
              placeholder="you@viganium.net"
              className={`${AUTH_INPUT} placeholder:!text-[var(--text-tertiary)] dark:placeholder:!text-[var(--text-secondary)]`}
            />
          </Field>

          <Field label={<AuthLabel>{t('auth.passwordLabel')}</AuthLabel>} required>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              dir="ltr"
              className={AUTH_INPUT}
            />
          </Field>
        </div>

        {/* The address typed so far goes along, so it need not be typed twice. */}
        <div className="mt-2 text-end">
          <Link to="/forgot-password" state={{ email }} className={AUTH_LINK}>
            {t('auth.forgotPassword')}
          </Link>
        </div>

        {error && <AuthAlert>{error}</AuthAlert>}

        <Button type="submit" variant="primary" size="lg" loading={busy} className="mt-5 w-full">
          {busy ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
      </form>
    </AuthShell>
  );
}
